import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {useAuthContext} from '../context/AuthContext';
import baseUrl from '../api/baseUrl';
import {handleResponseError} from '../api/handleResponseError';
import {NavigationRoutes} from '../NavigationRoutes';
import {useCreateAuthenticatedFetch} from '../api/useCreateAuthenticatedFetch';
import {
    assistantVoiceCapabilitiesQueryKey,
    assistantVoiceCapabilitiesStaleTime,
    fetchAssistantVoiceCapabilities
} from '../api/voice/useAssistantVoiceCapabilities';
import {queryClient} from '../lib/queryClient';
import unknownErrorToString from '../lib/unknownErrorToString';
import {
    capabilityDescriptionParameters,
    describeAssistantCapabilities
} from './voiceCapabilityDescription';
import {VoiceActionActivity} from './voiceActivity';
import {
    getVoiceAssistantResolutionMessage,
    isOpenableVoiceAssistant,
    type ResolvedVoiceAssistant
} from './voiceAssistantResolution';

export interface VoiceActionDefinition {
    id: string;
    label: string;
    description: string;
    parameters: Record<string, unknown>;
}

export interface VoiceActionResult {
    message: string;
    status?: 'ready' | 'loading' | 'unavailable';
    data?: unknown;
    speakText?: string;
}

export interface VoicePageContext {
    url: string;
    path: string;
    title: string;
    /**
     * Plain-language statement of where the user is and what is open, built
     * from the route plus facts pages publish through useVoicePageFact. The
     * model reads this instead of guessing from GUID-laden paths.
     */
    description: string;
    selectedText?: string;
    userEmail?: string;
    capabilities: VoiceActionDefinition[];
}

export interface VoiceToolDefinition {
    type: 'function';
    name: string;
    description: string;
    parameters: Record<string, unknown>;
}

interface VoiceSessionResponseBase {
    model: string;
    voice: string;
}

export interface LiveSessionResponse extends VoiceSessionResponseBase {
    protocol: 'live';
    sessionId: string;
    answerSdp: string;
    /** Serialized backend ModelName used for Responses delegation. */
    backendModel: string;
}

export interface AzureRealtimeSessionResponse extends VoiceSessionResponseBase {
    protocol: 'realtime';
    clientSecret: string;
    expiresAt?: number;
    realtimeCallsUrl: string;
}

export type VoiceSessionResponse =
    | LiveSessionResponse
    | AzureRealtimeSessionResponse;

export type VoiceActionHandler = (
    args: Record<string, unknown>
) => Promise<VoiceActionResult> | VoiceActionResult;

/**
 * Something a page wants the voice session to know about without the user
 * asking, such as a reply landing in the open thread. Pages announce
 * unconditionally; the widget decides what to do with it (speak it, tell the
 * model, or nothing when no session is open).
 */
export type VoiceAnnouncementKind =
    | 'assistant_reply'
    | 'run_started'
    /** A distinct tool activity began (searching the web, using a connector). */
    | 'run_progress'
    /** The run finished without a text reply; the summary says what it produced. */
    | 'run_completed'
    /** An MCP tool call is waiting for the user's approval. */
    | 'approval_pending'
    | 'run_failed';

export interface VoiceAnnouncement {
    kind: VoiceAnnouncementKind;
    /** One sentence for the model describing what changed. */
    summary: string;
    /** Full text to read aloud when the kind supports it. */
    text?: string;
    /** Identifies the underlying item so the same event is never delivered twice. */
    dedupeKey: string;
    assistantId?: string;
    threadId?: string;
    /** Structured detail for the model (outputs, activities, approval request). */
    data?: Record<string, unknown>;
}

export type VoiceAnnouncementListener = (
    announcement: VoiceAnnouncement
) => void;

/** Browser-side voice lifecycle events recorded for usage analytics. */
export type VoiceSessionEventKind =
    | 'ended'
    | 'idle_closed'
    | 'minute'
    | 'action_invoked';

interface RegisteredVoiceAction extends VoiceActionDefinition {
    handler: VoiceActionHandler;
    scopePath?: string;
    /** What the widget shows while this action runs; see voiceActivity.ts. */
    activity?: VoiceActionActivity;
}

interface VoiceActionRegistryContextValue {
    registerAction: (
        definition: VoiceActionDefinition,
        handler: VoiceActionHandler,
        options?: {scopePath?: string; activity?: VoiceActionActivity}
    ) => () => void;
    setPageFact: (key: string, fact: string | null) => void;
    /**
     * Same as VoiceControlSurfaceContextValue.announce, exposed here so pages
     * can announce without subscribing to actionVersion (see useVoiceAnnounce).
     */
    announce: (announcement: VoiceAnnouncement) => void;
}

interface VoiceControlSurfaceContextValue {
    actionVersion: number;
    getPageContext: () => VoicePageContext;
    createVoiceSession: (session: {
        sdp: string;
        /** Optional provider-specific model override. */
        model?: string;
        voice?: string;
        instructions?: string;
        tools?: VoiceToolDefinition[];
    }) => Promise<VoiceSessionResponse>;
    invokeAction: (
        actionId: string,
        args: Record<string, unknown>
    ) => Promise<VoiceActionResult>;
    /**
     * What to show while an action runs. Falls back to the action's own label
     * so every tool call is visible, not only the ones with custom wording.
     */
    getActionActivity: (actionId: string) => VoiceActionActivity | null;
    announce: (announcement: VoiceAnnouncement) => void;
    subscribeToAnnouncements: (
        listener: VoiceAnnouncementListener
    ) => () => void;
    /** Fire-and-forget usage tracking; failures are logged, never surfaced. */
    trackVoiceEvent: (
        event: VoiceSessionEventKind,
        assistantId?: string
    ) => void;
}

const VoiceActionRegistryContext =
    createContext<VoiceActionRegistryContextValue | null>(null);

const VoiceControlSurfaceContext =
    createContext<VoiceControlSurfaceContextValue | null>(null);

const actionParameters = {
    empty: {
        type: 'object',
        properties: {},
        additionalProperties: false
    },
    navigateKnownRoute: {
        type: 'object',
        properties: {
            route: {
                type: 'string',
                enum: ['home', 'library', 'threads']
            }
        },
        required: ['route'],
        additionalProperties: false
    },
    openAssistantByName: {
        type: 'object',
        properties: {
            name: {
                type: 'string',
                description:
                    'The assistant name, or enough of it to search. Omit when passing a candidate assistantId.'
            },
            assistantId: {
                type: 'string',
                description:
                    'Optional candidate assistantId from a previous suggestions or ambiguous response.'
            },
            assistantName: {
                type: 'string',
                description:
                    'Optional candidate assistantName from a previous suggestions or ambiguous response.'
            }
        },
        additionalProperties: false
    },
    describeAssistantByName: {
        type: 'object',
        properties: {
            name: {
                type: 'string',
                description:
                    'The assistant name, or enough of it to search. Omit when passing an assistantId.'
            },
            assistantId: {
                type: 'string',
                description:
                    'The assistantId when known, from a Home or Library read-back item or an earlier candidates response.'
            },
            assistantName: {
                type: 'string',
                description:
                    'Optional display name to use alongside assistantId.'
            },
            ...capabilityDescriptionParameters
        },
        additionalProperties: false
    }
};

const getStringArg = (args: Record<string, unknown>, name: string): string => {
    const value = args[name];
    return typeof value === 'string' ? value.trim() : '';
};

const getIntegerArg = (
    args: Record<string, unknown>,
    name: string
): number | undefined => {
    const value = args[name];
    const parsed =
        typeof value === 'number'
            ? value
            : typeof value === 'string'
              ? Number.parseInt(value, 10)
              : NaN;
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
};

const getSelectedText = (): string => window.getSelection()?.toString() ?? '';

const maxSelectedTextLength = 4_000;

const isActionInScope = (action: RegisteredVoiceAction, path: string) =>
    !action.scopePath || action.scopePath === path;

const describeRoute = (path: string): string => {
    if (path === NavigationRoutes.home()) {
        return 'The user is on the Home page, which lists their own assistants.';
    }
    if (path.startsWith(NavigationRoutes.library())) {
        return 'The user is on the Library page, which lists all assistants.';
    }
    if (path.startsWith(NavigationRoutes.threads())) {
        return 'The user is on the Threads page, which lists conversations across assistants.';
    }
    if (path.startsWith('/assistants/')) {
        return 'The user has an assistant open.';
    }
    if (path.startsWith('/admin')) {
        return 'The user is on an admin page.';
    }
    return `The user is on ${path}.`;
};

export const VoiceControlSurfaceProvider: React.FC<{
    children: React.ReactNode;
}> = ({children}) => {
    const actionsRef = useRef<Map<string, RegisteredVoiceAction>>(new Map());
    const announcementListenersRef = useRef<Set<VoiceAnnouncementListener>>(
        new Set()
    );
    const pageFactsRef = useRef<Map<string, string>>(new Map());
    const [actionVersion, setActionVersion] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const currentPathRef = useRef(location.pathname);
    const {currentUserEmail} = useAuthContext();
    const authenticatedFetch = useCreateAuthenticatedFetch();

    currentPathRef.current = location.pathname;

    const notifyActionsChanged = useCallback(() => {
        setActionVersion(version => version + 1);
    }, []);

    const registerAction = useCallback(
        (
            definition: VoiceActionDefinition,
            handler: VoiceActionHandler,
            options?: {scopePath?: string; activity?: VoiceActionActivity}
        ) => {
            const action: RegisteredVoiceAction = {
                ...definition,
                handler,
                scopePath: options?.scopePath,
                activity: options?.activity
            };
            actionsRef.current.set(definition.id, action);
            notifyActionsChanged();

            return () => {
                const existing = actionsRef.current.get(definition.id);
                if (existing?.handler === handler) {
                    actionsRef.current.delete(definition.id);
                    notifyActionsChanged();
                }
            };
        },
        [notifyActionsChanged]
    );

    const setPageFact = useCallback(
        (key: string, fact: string | null) => {
            const facts = pageFactsRef.current;
            const trimmed = fact?.trim() ?? '';
            if (facts.get(key) === (trimmed || undefined)) {
                return;
            }
            if (trimmed) {
                facts.set(key, trimmed);
            } else {
                facts.delete(key);
            }
            notifyActionsChanged();
        },
        [notifyActionsChanged]
    );

    const capabilitySnapshot = useCallback(
        (path = currentPathRef.current) =>
            Array.from(actionsRef.current.values())
                .filter(action => isActionInScope(action, path))
                // Destructure-to-omit: handler, scopePath, and activity are named
                // only so the rest spread drops them from the snapshot. They are
                // widget concerns; the model sees ids, labels, and descriptions.
                .map(
                    ({
                        handler: _handler,
                        scopePath: _scopePath,
                        activity: _activity,
                        ...definition
                    }) => definition
                ),
        []
    );

    const getPageContext = useCallback((): VoicePageContext => {
        const path = currentPathRef.current;
        const facts = Array.from(pageFactsRef.current.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, fact]) => fact);
        return {
            url: window.location.href,
            path,
            title: document.title,
            description: [describeRoute(path), ...facts].join(' '),
            selectedText: getSelectedText(),
            userEmail: currentUserEmail || undefined,
            capabilities: capabilitySnapshot(path)
        };
    }, [capabilitySnapshot, currentUserEmail]);

    const createVoiceSession = useCallback(
        async (session: {
            sdp: string;
            /** Optional provider-specific model override. */
            model?: string;
            voice?: string;
            instructions?: string;
            tools?: VoiceToolDefinition[];
        }): Promise<VoiceSessionResponse> => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/voice/session`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(session)
                }
            );

            if (!response.ok) {
                await handleResponseError(response);
            }

            return await response.json();
        },
        [authenticatedFetch]
    );

    const invokeAction = useCallback(
        async (
            actionId: string,
            args: Record<string, unknown>
        ): Promise<VoiceActionResult> => {
            const action = actionsRef.current.get(actionId);
            if (!action || !isActionInScope(action, currentPathRef.current)) {
                throw new Error(
                    `Action "${actionId}" is not available on the current page.`
                );
            }

            return await action.handler(args);
        },
        []
    );

    const getActionActivity = useCallback((actionId: string) => {
        const action = actionsRef.current.get(actionId);
        if (!action) {
            return null;
        }

        return action.activity ?? {label: action.label};
    }, []);

    const announce = useCallback((announcement: VoiceAnnouncement) => {
        announcementListenersRef.current.forEach(listener => {
            try {
                listener(announcement);
            } catch (error) {
                console.warn('Voice announcement listener failed', error);
            }
        });
    }, []);

    const subscribeToAnnouncements = useCallback(
        (listener: VoiceAnnouncementListener) => {
            announcementListenersRef.current.add(listener);
            return () => {
                announcementListenersRef.current.delete(listener);
            };
        },
        []
    );

    const trackVoiceEvent = useCallback(
        (event: VoiceSessionEventKind, assistantId?: string) => {
            authenticatedFetch(`${baseUrl}/api/voice/session-event`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({event, assistantId})
            }).catch(error => {
                console.debug('Voice usage event not recorded', event, error);
            });
        },
        [authenticatedFetch]
    );

    useEffect(() => {
        const routeMap: Record<string, string> = {
            home: NavigationRoutes.home(),
            library: NavigationRoutes.library(),
            threads: NavigationRoutes.threads()
        };

        // Turn name / candidate arguments into one assistant, or into the
        // result to hand back (a candidates question, or an error). Shared by
        // the open and describe actions so both follow the same candidate
        // contract.
        const resolveAssistant = async (
            args: Record<string, unknown>
        ): Promise<
            | {assistantId: string; assistantName: string | undefined}
            | {result: VoiceActionResult}
        > => {
            const candidateId = getStringArg(args, 'assistantId');
            const candidateName = getStringArg(args, 'assistantName');
            if (candidateId) {
                return {
                    assistantId: candidateId,
                    assistantName: candidateName || undefined
                };
            }

            const name = getStringArg(args, 'name') || candidateName;
            if (!name) {
                return {
                    result: {
                        message: 'Assistant name or candidate is required.'
                    }
                };
            }

            const response = await authenticatedFetch(
                `${baseUrl}/api/voice/resolve-assistant`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({name})
                }
            );
            if (!response.ok) {
                return {
                    result: {
                        message: `Unable to resolve assistant (${response.status}).`
                    }
                };
            }

            const assistant = (await response.json()) as ResolvedVoiceAssistant;
            if (!isOpenableVoiceAssistant(assistant)) {
                return {
                    result: {
                        message: getVoiceAssistantResolutionMessage(assistant),
                        data: assistant
                    }
                };
            }

            return {
                assistantId: assistant.assistantId,
                assistantName: assistant.assistantName
            };
        };

        const unregisterNavigate = registerAction(
            {
                id: 'navigate_known_route',
                label: 'Navigate',
                description:
                    'Navigate to a known Theta Assist route. Never use arbitrary URLs.',
                parameters: actionParameters.navigateKnownRoute
            },
            args => {
                const route = getStringArg(args, 'route');
                const path = routeMap[route];
                if (!path) {
                    return {
                        message: `Unknown route "${route}".`,
                        data: {knownRoutes: Object.keys(routeMap)}
                    };
                }
                navigate(path);
                return {message: `Navigated to ${route}.`, data: {path}};
            }
        );

        const unregisterSelectedText = registerAction(
            {
                id: 'read_selected_text',
                label: 'Read Selected Text',
                description:
                    'Read the text the user currently has highlighted on the page. Use this for requests like "explain the text I selected", "what does the highlighted part say", or "summarise my selection".',
                parameters: actionParameters.empty
            },
            () => {
                const selectedText = getSelectedText().trim();
                if (!selectedText) {
                    return {
                        message: 'No text is selected on the page.',
                        data: {selectedText: ''}
                    };
                }

                const truncated = selectedText.length > maxSelectedTextLength;
                return {
                    message: truncated
                        ? `Selected text captured (first ${maxSelectedTextLength} characters).`
                        : 'Selected text captured.',
                    data: {
                        selectedText: selectedText.slice(
                            0,
                            maxSelectedTextLength
                        ),
                        truncated,
                        totalCharacters: selectedText.length
                    }
                };
            }
        );

        // Global so the user can switch assistant from inside another assistant
        // without going via Home first. No scope: any assistant the user can
        // access, resolved server-side with the same candidate contract as the
        // page-scoped variants.
        // Opening an assistant loads its thread list; describing one calls the
        // capabilities endpoint. Both are slow enough to look like silence.
        const unregisterOpenAssistant = registerAction(
            {
                id: 'open_assistant_by_name',
                label: 'Open Assistant',
                description:
                    'Open any assistant the user has access to by name, from any page, including switching from one assistant to another. If a previous suggestions or ambiguous response contains candidates and the user chooses one, call this with that candidate assistantId and assistantName.',
                parameters: actionParameters.openAssistantByName
            },
            async args => {
                const resolved = await resolveAssistant(args);
                if ('result' in resolved) {
                    return resolved.result;
                }
                navigate(
                    NavigationRoutes.assistants.detail(resolved.assistantId)
                );
                return {
                    message: resolved.assistantName
                        ? `Opened assistant ${resolved.assistantName}.`
                        : 'Opened assistant.',
                    data: {
                        status: 'resolved',
                        assistantId: resolved.assistantId,
                        assistantName: resolved.assistantName
                    }
                };
            },
            {activity: {label: 'Opening the assistant', icon: 'assistant'}}
        );

        // Answers "what can the X assistant do" from anywhere without opening
        // it, from the same capability profile the open assistant's describe
        // action reads. Server and client both cache the profile for a few
        // minutes, so browsing Home or Library and asking about several
        // assistants stays cheap.
        const unregisterDescribeAssistant = registerAction(
            {
                id: 'describe_assistant_by_name',
                label: 'Describe Assistant By Name',
                description:
                    'Describe what any assistant the user has access to can do, without opening it: its connected services, tools, knowledge files, skills, suggested prompts, or user guide. Use for "what can the X assistant do", "does X have access to my calendar", or "what does the second one do" after a Home or Library read-back (pass that item\'s assistantId). Never guess capabilities. When the assistant in question is the open one, describe_current_assistant is equivalent.',
                parameters: actionParameters.describeAssistantByName
            },
            async args => {
                const resolved = await resolveAssistant(args);
                if ('result' in resolved) {
                    return resolved.result;
                }
                const part = getStringArg(args, 'part') || 'overview';
                try {
                    const info = await queryClient.fetchQuery({
                        queryKey: assistantVoiceCapabilitiesQueryKey(
                            resolved.assistantId
                        ),
                        queryFn: () =>
                            fetchAssistantVoiceCapabilities(
                                authenticatedFetch,
                                resolved.assistantId
                            ),
                        staleTime: assistantVoiceCapabilitiesStaleTime
                    });
                    const description = describeAssistantCapabilities(
                        info,
                        part,
                        getIntegerArg(args, 'offset') ?? 0
                    );
                    return {
                        ...description,
                        data: {
                            assistantId: resolved.assistantId,
                            assistantName: resolved.assistantName ?? info.name,
                            part,
                            detail: description.data
                        }
                    };
                } catch (error) {
                    const text = `I could not load what ${
                        resolved.assistantName ?? 'that assistant'
                    } can do right now.`;
                    return {
                        status: 'unavailable' as const,
                        message: text,
                        speakText: text,
                        data: {
                            assistantId: resolved.assistantId,
                            error: unknownErrorToString(error)
                        }
                    };
                }
            },
            {
                activity: {
                    label: 'Checking what the assistant can do',
                    icon: 'assistant'
                }
            }
        );

        return () => {
            unregisterNavigate();
            unregisterSelectedText();
            unregisterOpenAssistant();
            unregisterDescribeAssistant();
        };
    }, [authenticatedFetch, navigate, registerAction]);

    useEffect(() => {
        notifyActionsChanged();
    }, [location.pathname, notifyActionsChanged]);

    const contextValue = useMemo(
        () => ({
            actionVersion,
            getPageContext,
            createVoiceSession,
            invokeAction,
            getActionActivity,
            announce,
            subscribeToAnnouncements,
            trackVoiceEvent
        }),
        [
            actionVersion,
            announce,
            createVoiceSession,
            getActionActivity,
            getPageContext,
            invokeAction,
            subscribeToAnnouncements,
            trackVoiceEvent
        ]
    );

    const registryContextValue = useMemo(
        () => ({registerAction, setPageFact, announce}),
        [announce, registerAction, setPageFact]
    );

    return (
        <VoiceActionRegistryContext.Provider value={registryContextValue}>
            <VoiceControlSurfaceContext.Provider value={contextValue}>
                {children}
            </VoiceControlSurfaceContext.Provider>
        </VoiceActionRegistryContext.Provider>
    );
};

/**
 * Registers a voice action for as long as the component is mounted and
 * `enabled` holds. Actions are scoped to the path they were registered on;
 * pass `global` for actions that belong to app-wide chrome such as the voice
 * widget, so they stay available on every page.
 */
export function useVoiceAction(
    definition: VoiceActionDefinition,
    handler: VoiceActionHandler,
    options?: {
        enabled?: boolean;
        global?: boolean;
        /** Shown in the widget while this action runs; see voiceActivity.ts. */
        activity?: VoiceActionActivity;
    }
) {
    const context = useContext(VoiceActionRegistryContext);
    const location = useLocation();
    const enabled = options?.enabled ?? true;
    const isGlobal = options?.global ?? false;
    const activityLabel = options?.activity?.label;
    const activityIcon = options?.activity?.icon;

    // Callers routinely rebuild the handler (and sometimes the definition) on
    // every render. Re-registering bumps the registry's actionVersion, which
    // re-renders every consumer of the control surface, and if the caller is
    // one of them that is an infinite loop (React #185; seen on a thread
    // awaiting an MCP approval). So the registration reads the latest handler
    // and definition through refs and only re-runs when the action's content
    // actually changes.
    const definitionRef = useRef(definition);
    const handlerRef = useRef(handler);
    useLayoutEffect(() => {
        definitionRef.current = definition;
        handlerRef.current = handler;
    });

    const definitionSignature = useMemo(
        () =>
            JSON.stringify([
                definition.id,
                definition.label,
                definition.description,
                definition.parameters
            ]),
        [definition]
    );

    useLayoutEffect(() => {
        if (!context || !enabled) {
            return;
        }

        const stableHandler: VoiceActionHandler = (...args) =>
            handlerRef.current(...args);

        return context.registerAction(definitionRef.current, stableHandler, {
            scopePath: isGlobal ? undefined : location.pathname,
            activity: activityLabel
                ? {label: activityLabel, icon: activityIcon}
                : undefined
        });
        // definitionSignature stands in for definition: a fresh object with
        // the same content must not re-register the action.
    }, [
        activityIcon,
        activityLabel,
        context,
        definitionSignature,
        enabled,
        isGlobal,
        location.pathname
    ]);
}

/**
 * Publishes one plain-language fact about the current page for the voice
 * prompt, such as which assistant or thread is open. Keyed so a page and a
 * nested view can each contribute; cleared on unmount or when `fact` is null.
 */
export function useVoicePageFact(key: string, fact: string | null) {
    const context = useContext(VoiceActionRegistryContext);

    useLayoutEffect(() => {
        if (!context) {
            return;
        }
        context.setPageFact(key, fact);
        return () => context.setPageFact(key, null);
    }, [context, fact, key]);
}

/**
 * Announce to the voice session without subscribing to the control surface.
 * Pages should use this rather than useVoiceControlSurface().announce: the
 * surface value changes on every action registration, so a page consuming it
 * re-renders each time any of its own actions re-register.
 */
export function useVoiceAnnounce() {
    const context = useContext(VoiceActionRegistryContext);

    if (!context) {
        throw new Error(
            'useVoiceAnnounce must be used within VoiceControlSurfaceProvider.'
        );
    }

    return context.announce;
}

export function useVoiceControlSurface() {
    const context = useContext(VoiceControlSurfaceContext);

    if (!context) {
        throw new Error(
            'useVoiceControlSurface must be used within VoiceControlSurfaceProvider.'
        );
    }

    return context;
}
